import { useState, useEffect } from 'react';
import HomePage from './components/HomePage';
import ProjectDetailPage from './components/ProjectDetailPage';
import SolutionsPage from './components/SolutionsPage';
import { SOLUTIONS } from './components/SolutionsPage';


// Sample projects data (in a real app, this would likely come from an API)

const PROJECTS = [
  {
    id: 1,
    title: 'Intelligent Process Automation',
    description: 'AI-powered workflow automation to eliminate repetitive tasks.',
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=600&h=400&auto=format&fit=crop',
    category: 'Enterprise',
    fullDescription: 'Our Intelligent Process Automation (IPA) platform combines artificial intelligence, machine learning, and robotic process automation to create a powerful solution that streamlines workflows, reduces errors, and frees your team to focus on high-value work.',
    date: 'April 15, 2025',
    readTime: '5 min read',
    author: 'Rahul',
    tags: ['Automation', 'AI', 'Enterprise', 'Workflow'],
    content: [
      {
        type: 'paragraph',
        content: "In today’s competitive landscape, organizations must find ways to optimize every process without sacrificing quality. Our Intelligent Process Automation (IPA) platform harnesses AI and RPA to eliminate tedious manual tasks, reducing human error and accelerating cycle times. By seamlessly integrating with your existing systems, it empowers teams to focus on strategic work rather than repetitive data entry. Continuous learning algorithms monitor operations in real time, surfacing opportunities for further improvement. The result is a more agile, cost‑effective enterprise that adapts as quickly as your business evolves."
      },
      {
        type: 'heading',
        content: 'The Power of AI-Driven Automation'
      },
      {
        type: 'paragraph',
        content: 'Our IPA platform leverages advanced machine learning algorithms to understand, learn, and automate complex business processes. Unlike traditional automation tools that require rigid, rule-based programming, our solution adapts and improves over time, identifying optimization opportunities and suggesting enhancements.'
      },
      {
        type: 'paragraph',
        content: 'The system continuously monitors process execution, identifies bottlenecks, and recommends optimizations. This self-improving capability ensures your automation solution evolves alongside your business needs.'
      },
      {
        type: 'heading',
        content: 'Key Features and Benefits'
      },
      {
        type: 'list',
        items: [
          'Intelligent Document Processing: Extract, categorize, and process information from various document types using advanced computer vision and NLP.',
          'Adaptive Workflow Management: Dynamic workflows that adjust based on real-time data and changing conditions.',
          'Process Mining & Optimization: Automatically identify inefficiencies and optimization opportunities in your business processes.',
          'Human-in-the-Loop Collaboration: Seamlessly combine AI automation with human expertise for optimal results.'
        ]
      }
    ]
  },
  {
    id: 2,
    title: 'RAG applications',
    description: 'Advanced language understanding for customer support and data extraction.',
    image: 'https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    category: 'Retrieval-Augmented Generation',
    fullDescription: 'Our Natural Language Processing Suite leverages state-of-the-art language models to analyze, understand, and generate human language, enabling businesses to extract valuable insights from unstructured text data and create more natural conversational experiences.',
    date: 'March 28, 2025',
    readTime: '4 min read',
    author: 'Rohith',
    tags: ['NLP', 'AI', 'Language', 'Machine Learning'],
    content: [
      {
        type: 'paragraph',
        content: 'Language is the cornerstone of human communication. Our Natural Language Processing Suite brings the power of advanced AI to understand and process human language at scale. By integrating powerful vector databases with Retrieval-Augmented Generation (RAG), we enhance the capabilities of large language models to deliver responses grounded in real-time, context-aware data.'
      },
      {
        type: 'heading',
        content: 'Unlocking the Value of Unstructured Text'
      },
      {
        type: 'paragraph',
        content: 'Approximately 80% of business data is unstructured, with a significant portion being text. Our NLP Suite helps organizations unlock the value hidden in documents, emails, support tickets, social media, and other text sources. Through vector search and semantic retrieval, businesses can extract relevant information instantly, improving decision-making and operational efficiency.'
      },
      {
        type: 'paragraph',
        content: 'Vector databases enable rapid similarity searches by embedding data into high-dimensional vectors that capture semantic meaning. When paired with RAG workflows, this allows LLMs to retrieve precise, domain-specific content from internal knowledge bases before generating responses—improving accuracy and reducing hallucinations.'
      },
      {
        type: 'paragraph',
        content: 'From customer support automation to intelligent search and personalized recommendations, our solutions bring the latest in AI-driven retrieval and generation into real-world enterprise use cases. This powerful combination of technologies makes it possible to deploy scalable, explainable, and context-sensitive AI applications across industries.'
      }
    ]
  },
  {
    id: 3,
    title: 'Predictive Analytics Platform',
    description: 'Harness historical data to predict future business outcomes.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=600&h=400&auto=format&fit=crop',
    category: 'Analytics',
    fullDescription: 'Our Predictive Analytics Platform transforms your historical data into actionable forecasts and insights. By identifying patterns and trends, our platform enables data-driven decision making that optimizes business outcomes and creates competitive advantage.',
    date: 'February 15, 2025',
    readTime: '6 min read',
    author: 'Rohith',
    tags: ['Analytics', 'Forecasting', 'Data Science', 'AI'],
    content: [
      {
        type: 'paragraph',
        content: 'Data is the new oil, but extracting insights requires more than just storage and visualization. Our Predictive Analytics Platform applies cutting-edge statistical models and machine learning techniques to your historical data, uncovering hidden trends and forecasting future outcomes with high confidence. Whether you’re modeling customer churn, sales growth, or operational risk, our solution delivers clear, actionable projections. Integration with your data warehouse ensures seamless onboarding, and intuitive dashboards let you explore scenarios in real time. Drive strategic decisions and stay ahead of market shifts with precise, data-backed foresight.'
      },
      {
        type: 'heading',
        content: 'From Hindsight to Foresight'
      },
      {
        type: 'paragraph',
        content: 'Most organizations have robust reporting capabilities that tell them what happened in the past. Our platform takes you beyond retrospective analysis to predictive and prescriptive insights that inform future strategy.'
      }
    ]
  },
  {
    id: 4,
    title: 'Computer Vision System',
    description: 'Visual recognition technology for security and quality assurance.',
    image: 'https://images.unsplash.com/photo-1501621667575-af81f1f0bacc?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    category: 'Computer Vision',
    fullDescription: 'Our Computer Vision System enables machines to "see" and interpret visual information with near-human accuracy. From security and surveillance to quality control and asset management, our solution transforms visual data into actionable insights.',
    date: 'January 20, 2025',
    readTime: '5 min read',
    author: 'Rahul',
    tags: ['Computer Vision', 'AI', 'Security', 'Quality Assurance'],
    content: [
      {
        type: 'paragraph',
        content: 'Vision is our most dominant sense, providing humans with rich information about our surroundings. Our Computer Vision System brings this capability to machines, using deep convolutional neural networks to detect, classify, and track objects in real time. From inspecting product defects on the assembly line to monitoring premises for security breaches, our solution scales across cameras and locations. Customizable filters and alert thresholds let you fine‑tune performance to your use case, while edge‑optimized models ensure low latency even in bandwidth‑constrained environments. Unlock a new dimension of operational intelligence with automated visual insights.'
      }
    ]
  },
  {
    id: 5,
    title: 'Autonomous AI Agents',
    description: 'Self-improving AI system for complex business decision-making.',
    image: 'https://images.unsplash.com/photo-1684369175809-f9642140a1bd?q=80&w=2142&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    category: 'Autonomous Systems',
    fullDescription: 'Our Autonomous Decision Engine combines multiple AI technologies to create a system capable of making complex business decisions with minimal human intervention. The system continuously learns and improves through feedback loops.',
    date: 'March 5, 2025',
    readTime: '7 min read',
    author: 'Rahul',
    tags: ['Autonomous Systems', 'AI', 'Decision Making', 'Machine Learning'],
    content: [
      {
        type: 'paragraph',
        content: 'In fast-moving business environments, the ability to make timely, data-driven decisions is critical. Our Autonomous AI Agents integrate reinforcement learning, knowledge graphs, and predictive models into a cohesive engine that evaluates options, weighs risks, and recommends or executes actions autonomously. Continuous feedback loops allow the system to learn from outcomes, refining its strategies over time. Human‑in‑the‑loop controls ensure oversight and compliance, while modular APIs let you tailor decision logic to your domain. Empower your organization with an AI partner that operates at machine scale and human ingenuity.'
      }
    ]
  },
  {
    id: 6,
    title: 'Edge AI Deployment',
    description: 'Powerful AI capabilities that run locally on edge devices.',
    image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?q=80&w=600&h=400&auto=format&fit=crop',
    category: 'Edge Computing',
    fullDescription: 'Our Edge AI Deployment solution brings the power of artificial intelligence to edge devices, enabling real-time processing without cloud connectivity. This approach reduces latency, enhances privacy, and enables AI in bandwidth-constrained environments.',
    date: 'February 28, 2025',
    readTime: '4 min read',
    author: 'Rohith',
    tags: ['Edge Computing', 'AI', 'IoT', 'Embedded Systems'],
    content: [
      {
        type: 'paragraph',
        content: 'The proliferation of IoT devices and the need for real-time AI processing is driving a shift toward edge computing. Our Edge AI Deployment solution optimizes and compresses state‑of‑the‑art models to run efficiently on resource‑constrained devices, ensuring rapid inference with minimal power draw. By processing data locally, you eliminate round‑trip latency to the cloud and reduce bandwidth costs while preserving data privacy. Centralized orchestration tools let you deploy updates and monitor performance across fleets of devices. Bring intelligence to the point of action and unlock new possibilities in surveillance, manufacturing, and beyond.'
      }
    ]
  }
];


// Main App Component with Routing
export default function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentSolutionId, setCurrentSolutionId] = useState(null);  // Listen for URL changes to handle browser navigation
  useEffect(() => {
    const handleNavigation = () => {
      const path = window.location.pathname;
      
      if (path === '/' || path === '/index.html') {
        setCurrentPage('home');
        setCurrentProjectId(null);
        setCurrentSolutionId(null);
      } else if (path.startsWith('/projects/')) {
        const id = parseInt(path.split('/').pop());
        if (!isNaN(id)) {
          setCurrentPage('project');
          setCurrentProjectId(id);
          setCurrentSolutionId(null);
        }
      } else if (path === '/solutions') {
        setCurrentPage('solutions');
        setCurrentProjectId(null);
        setCurrentSolutionId(null);
      } else if (path.startsWith('/solutions/')) {
        const id = parseInt(path.split('/').pop());
        if (!isNaN(id)) {
          setCurrentPage('solutions');
          setCurrentProjectId(null);
          setCurrentSolutionId(id);
        }
      }
    };

    // Initial check
    handleNavigation();

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleNavigation);
    
    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, []);

  // Navigation function that updates both URL and state
  const handleProjectClick = (projectId) => {
    window.history.pushState({}, '', `/projects/${projectId}`);
    setCurrentPage('project');
    setCurrentProjectId(projectId);
    setCurrentSolutionId(null);
  };

  // Navigation function for solutions
  const handleSolutionClick = (solutionId) => {
    window.history.pushState({}, '', `/solutions/${solutionId}`);
    setCurrentPage('solutions');
    setCurrentProjectId(null);
    setCurrentSolutionId(solutionId);
  };

  // Function to go back to home
  const goToHome = () => {
    window.history.pushState({}, '', '/');
    setCurrentPage('home');
    setCurrentProjectId(null);
    setCurrentSolutionId(null);
  };



  // Find the current project if on a project page
  const currentProject = currentProjectId 
    ? PROJECTS.find(p => p.id === currentProjectId)
    : null;

  // Find the current solution if on a solution page
  const currentSolution = currentSolutionId 
    ? SOLUTIONS.find(s => s.id === currentSolutionId)
    : null;

  // Render the appropriate page
  if (currentPage === 'home') {
    return <HomePage projects={PROJECTS} onProjectClick={handleProjectClick} />;
  } else if (currentPage === 'project' && currentProject) {
    return <ProjectDetailPage project={currentProject} goToHome={goToHome} />;
  } else if (currentPage === 'solutions') {
    return <SolutionsPage onSolutionClick={handleSolutionClick} />;
  } else {
    // 404 Page
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Page Not Found</h1>
          <p className="text-gray-400 mb-8">The page you're looking for doesn't exist or has been removed.</p>
          <button 
            onClick={goToHome}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }
}